import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import { isApiConfigured, loadServerConfig, isDevelopment, isOrderCreationAllowed } from '@/api/config'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: { requiresConfig: true }
    },
    {
      path: '/create-ad',
      name: 'create-ad',
      // Lazy-loaded for better performance
      component: () => import('@/views/CreateAdView.vue'),
      meta: { requiresConfig: true }
    },
    {
      path: '/no-tenant',
      name: 'no-tenant',
      component: () => import('@/views/NoTenantView.vue'),
      meta: { requiresConfig: false }
    },
    {
      path: '/setup',
      name: 'setup',
      component: () => import('@/views/SetupView.vue'),
      meta: { requiresConfig: false, devOnly: true }
    },
    // Admin routes
    {
      path: '/admin',
      name: 'admin-login',
      component: () => import('@/views/admin/AdminLoginView.vue'),
      meta: { requiresConfig: false, isAdmin: true }
    },
    {
      path: '/admin/tenants',
      name: 'admin-tenants',
      component: () => import('@/views/admin/AdminTenantsView.vue'),
      meta: { requiresConfig: false, isAdmin: true, requiresAuth: true }
    },
    {
      path: '/admin/tenants/new',
      name: 'admin-tenant-new',
      component: () => import('@/views/admin/AdminTenantEditView.vue'),
      meta: { requiresConfig: false, isAdmin: true, requiresAuth: true }
    },
    {
      path: '/admin/tenants/:id',
      name: 'admin-tenant-edit',
      component: () => import('@/views/admin/AdminTenantEditView.vue'),
      meta: { requiresConfig: false, isAdmin: true, requiresAuth: true }
    }
  ]
})

// Track if we've loaded the server config
let configLoaded = false

// Simple admin auth state (in-memory, clears on page refresh)
let isAdminAuthenticated = false

export function setAdminAuthenticated(value: boolean) {
  isAdminAuthenticated = value
}

export function getAdminAuthenticated(): boolean {
  return isAdminAuthenticated
}

// Navigation guard to check if API is configured
router.beforeEach(async (to, _from, next) => {
  // Load server config on first navigation
  if (!configLoaded) {
    await loadServerConfig()
    configLoaded = true
  }
  
  // Handle admin routes
  if (to.meta.isAdmin) {
    // Check if admin route requires authentication
    if (to.meta.requiresAuth && !isAdminAuthenticated) {
      next({
        name: 'admin-login',
        query: { returnTo: to.fullPath }
      })
      return
    }
    // Admin routes don't need API config
    next()
    return
  }
  
  // If going to setup page
  if (to.name === 'setup') {
    // Only allow setup in development mode
    if (!isDevelopment()) {
      next({ name: 'no-tenant' })
      return
    }
    // In dev mode, if already configured, go to home
    if (isApiConfigured()) {
      next({ name: 'home' })
      return
    }
    next()
    return
  }
  
  // If the route requires configuration and API is not configured
  if (to.meta.requiresConfig && !isApiConfigured()) {
    // In production without config, show the no-tenant page
    // In development, show the setup page
    if (isDevelopment()) {
      next({ name: 'setup' })
    } else {
      next({ name: 'no-tenant' })
    }
    return
  }

  // Order creation can be disabled independently at the tenant or site level. This is a UI
  // policy guard; the AccountScout API remains protected only by its existing API key model.
  if (to.name === 'create-ad' && !isOrderCreationAllowed()) {
    next({ name: 'home', query: to.query })
    return
  }
  
  next()
})

export default router
